import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  FORMAT_TEXT_COMMAND,
  LexicalNode,
  RangeSelection,
  $getSelection,
  $isRangeSelection,
} from 'lexical';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode,
} from '@lexical/list';
import { TOGGLE_LINK_COMMAND, $isLinkNode } from '@lexical/link';
import Button from 'react-bootstrap/Button';
import i18next from 'i18next';
import { ReactElement, useEffect, useState } from 'react';
import { FaBold, FaItalic, FaUnderline, FaListUl, FaListOl, FaLink, FaUnlink } from 'react-icons/fa';

function getListTypeForSelection(selection: RangeSelection): 'bullet' | 'number' | null {
  const anchorNode = selection.anchor.getNode();
  let parent = anchorNode.getParent();

  while (parent !== null) {
    if ($isListNode(parent)) {
      return parent.getListType() as 'bullet' | 'number';
    }
    parent = parent.getParent();
  }

  return null;
}

function isLinkSelection(selection: RangeSelection): boolean {
  const anchorNode: LexicalNode = selection.anchor.getNode();

  return $isLinkNode(anchorNode) || $isLinkNode(anchorNode.getParent());
}

export function LexicalToolbar(): ReactElement {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState<boolean>(false);
  const [isItalic, setIsItalic] = useState<boolean>(false);
  const [isUnderline, setIsUnderline] = useState<boolean>(false);
  const [blockType, setBlockType] = useState<'number' | 'bullet' | null>(null);
  const [isLink, setIsLink] = useState<boolean>(false);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          setIsBold(selection.hasFormat('bold'));
          setIsItalic(selection.hasFormat('italic'));
          setIsUnderline(selection.hasFormat('underline'));

          const listType = getListTypeForSelection(selection);
          setBlockType(listType);
          setIsLink(isLinkSelection(selection));
        }
      });
    });
  }, [editor]);

  return (
    <div className="d-flex flex-wrap align-items-center gap-2 mb-2 border-bottom pb-2">
      <div className="d-flex gap-2">
        <Button
          variant={isBold ? 'primary' : 'light'}
          className="px-2 py-1 border"
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}>
          <FaBold />
        </Button>

        <Button
          variant={isItalic ? 'primary' : 'light'}
          className="px-2 py-1 border"
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}>
          <FaItalic />
        </Button>

        <Button
          variant={isUnderline ? 'primary' : 'light'}
          className="px-2 py-1 border"
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}>
          <FaUnderline />
        </Button>
      </div>

      <div style={{ width: '16px' }}></div>

      <div className="d-flex gap-2">
        <Button
          variant={blockType === 'number' ? 'primary' : 'light'}
          className="px-2 py-1 border"
          onClick={() =>
            editor.dispatchCommand(
              blockType === 'number'
                ? REMOVE_LIST_COMMAND
                : INSERT_ORDERED_LIST_COMMAND,
              undefined
            )
          }>
          <FaListOl />
        </Button>

        <Button
          variant={blockType === 'bullet' ? 'primary' : 'light'}
          className="px-2 py-1 border"
          onClick={() =>
            editor.dispatchCommand(
              blockType === 'bullet'
                ? REMOVE_LIST_COMMAND
                : INSERT_UNORDERED_LIST_COMMAND,
              undefined
            )
          }>
          <FaListUl />
        </Button>
      </div>

      <div style={{ width: '16px' }}></div>

      <Button
        variant={isLink ? 'primary' : 'light'}
        className="px-2 py-1 border"
        title={i18next.t(isLink ? 'generic.lexicalToolbarRemoveLink' : 'generic.lexicalToolbarInsertLink')}
        onClick={() => {
          if (isLink) {
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
            return;
          }

          const url: string | null = window.prompt(i18next.t('generic.lexicalToolbarLinkPrompt'));

          if (url === null || url.trim() === '') {
            return;
          }

          editor.dispatchCommand(TOGGLE_LINK_COMMAND, url.trim());
        }}>
        {isLink ? <FaUnlink /> : <FaLink />}
      </Button>
    </div>
  );
}
